import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider,
  LinearProgress,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowForward as ArrowForwardIcon,
  Task as TaskIcon,
} from '@mui/icons-material';
import { ApiService, ApiEndpoints } from '../../services/ApiService';
import { usePermissions } from '../../hooks/usePermissions';
import { Permission } from '../../types/user';

/**
 * Requirement interface
 */
interface Requirement {
  id: string | number;
  name: string;
  description?: string;
  status: 'completed' | 'in-progress' | 'not-started' | 'archived';
  progress?: number;
  controlId: string | number;
  controlName?: string;
  obligationId?: string | number;
  obligationName?: string;
  frameworkId?: string | number;
  frameworkName?: string;
  createdAt: string;
  updatedAt: string;
  dueDate?: string;
  assignedTo?: string;
  tasks?: Task[];
  metadata?: Record<string, any>;
}

/**
 * Task interface
 */
interface Task {
  id: string | number;
  name: string;
  description?: string;
  status: string;
  dueDate?: string;
  assignedTo?: string;
}

/**
 * Material Requirement Detail Page
 * 
 * Displays detailed information about a requirement
 * Includes tabs for requirement details, tasks, and metadata
 */
export default function MaterialRequirementDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [requirement, setRequirement] = useState<Requirement | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  
  // Check permissions
  const canEdit = hasPermission(Permission.REQUIREMENT_EDIT);
  const canDelete = hasPermission(Permission.REQUIREMENT_DELETE);

  /**
   * Fetch requirement details from API
   */
  useEffect(() => {
    const fetchRequirementDetail = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const response = await ApiService.get<Requirement>(ApiEndpoints.REQUIREMENTS.DETAIL(id));
        setRequirement(response);
      } catch (err) {
        console.error('Error fetching requirement detail:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
        
        // Fallback to mock data for development
        setRequirement({
          id,
          name: 'Document Approval Process',
          description: 'Create formal approval process for security policy documents',
          status: 'in-progress',
          progress: 60,
          controlId: 1,
          controlName: 'Security Policy Document',
          obligationId: 1,
          obligationName: 'Information Security Policy',
          frameworkId: 1,
          frameworkName: 'ISO 27001',
          createdAt: '2023-01-15T00:00:00Z',
          updatedAt: '2023-02-10T00:00:00Z',
          dueDate: '2023-03-31T00:00:00Z',
          assignedTo: 'John Smith',
          tasks: [
            { id: 1, name: 'Draft approval workflow', description: 'Create initial workflow diagram', status: 'completed', dueDate: '2023-02-01T00:00:00Z' },
            { id: 2, name: 'Get management sign-off', description: 'Present to management for approval', status: 'in-progress', dueDate: '2023-03-15T00:00:00Z' },
            { id: 3, name: 'Document in policy', description: 'Add to policy documentation', status: 'not-started', dueDate: '2023-03-31T00:00:00Z' },
          ],
          metadata: {
            priority: 'High',
            reviewFrequency: 'Annual',
            lastReviewed: '2023-01-15',
          }
        });
      } finally {
        setLoading(false);
      }
    };

    fetchRequirementDetail();
  }, [id]);

  /**
   * Handle tab change
   */
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  /**
   * Handle requirement delete
   */
  const handleDelete = async () => {
    if (!id || !requirement) return;
    
    if (window.confirm(`Are you sure you want to delete the requirement "${requirement.name}"?`)) {
      try {
        await ApiService.delete(ApiEndpoints.REQUIREMENTS.DELETE(id));
        navigate('/requirements');
      } catch (err) {
        console.error('Error deleting requirement:', err);
        alert('Failed to delete requirement. Please try again.');
      }
    }
  };

  /**
   * Get status chip color based on status
   */
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in-progress':
        return 'info';
      case 'not-started':
        return 'warning';
      case 'archived':
        return 'error';
      default:
        return 'default';
    }
  };

  /**
   * Get status label
   */
  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'in-progress':
        return 'In Progress';
      case 'not-started':
        return 'Not Started';
      default:
        return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  /**
   * Format date
   */
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Not set';
    
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  /**
   * Loading state
   */
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  /**
   * Error state
   */
  if (error || !requirement) {
    return (
      <Box sx={{ my: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          component={Link}
          to="/requirements"
          sx={{ mb: 2 }}
        >
          Back to Requirements
        </Button>
        <Alert severity="error">
          {error || 'Requirement not found'}
        </Alert>
      </Box>
    );
  }

  /**
   * Render component
   */
  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
        <Box>
          <Button
            startIcon={<ArrowBackIcon />}
            component={Link}
            to="/requirements"
            sx={{ mb: 1 }}
          >
            Back to Requirements
          </Button>
          <Typography variant="h4">
            {requirement.name}
          </Typography>
        </Box>
        <Box>
          {canEdit && (
            <Button
              id="edit-requirement-button"
              data-testid="edit-button"
              variant="outlined"
              startIcon={<EditIcon />}
              component={Link}
              to={`/requirements/${id}/edit`}
              sx={{ mr: 1 }}
            >
              Edit
            </Button>
          )}
          {canDelete && (
            <Button
              id="delete-requirement-button"
              data-testid="delete-button"
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDelete}
            >
              Delete
            </Button>
          )}
        </Box>
      </Box>
      
      {/* Status and progress */}
      <Box mb={3}>
        <Box display="flex" alignItems="center" mb={1}>
          <Chip
            label={getStatusLabel(requirement.status)}
            color={getStatusColor(requirement.status) as any}
            size="small"
            sx={{ mr: 2 }}
          />
          
          {requirement.assignedTo && (
            <Typography variant="body2" color="text.secondary">
              Assigned to: {requirement.assignedTo}
            </Typography>
          )}
        </Box>
        
        {requirement.progress !== undefined && (
          <Box sx={{ width: '100%' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
              <Typography variant="body2" color="text.secondary">Progress</Typography>
              <Typography variant="body2" color="text.secondary">{requirement.progress}%</Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={requirement.progress} 
              color={requirement.progress === 100 ? 'success' : 'primary'}
              sx={{ height: 8, borderRadius: 1 }}
            />
          </Box>
        )}
      </Box>
      
      {/* Related items */}
      <Box display="flex" flexWrap="wrap" mb={3} gap={1}>
        {requirement.controlName && (
          <Button
            size="small"
            variant="outlined"
            component={Link}
            to={`/controls/${requirement.controlId}`}
            endIcon={<ArrowForwardIcon fontSize="small" />}
          >
            {requirement.controlName}
          </Button>
        )}
        
        {requirement.obligationName && (
          <Button
            size="small"
            variant="outlined"
            component={Link}
            to={`/obligations/${requirement.obligationId}`}
            endIcon={<ArrowForwardIcon fontSize="small" />}
          >
            {requirement.obligationName}
          </Button>
        )}
        
        {requirement.frameworkName && (
          <Button
            size="small"
            variant="outlined"
            component={Link}
            to={`/frameworks/${requirement.frameworkId}`}
            endIcon={<ArrowForwardIcon fontSize="small" />}
          >
            {requirement.frameworkName}
          </Button>
        )}
      </Box>
      
      {/* Description */}
      {requirement.description && (
        <Typography variant="body1" sx={{ mb: 3 }}>
          {requirement.description}
        </Typography>
      )}
      
      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Details" />
          <Tab label="Tasks" />
          <Tab label="Metadata" />
        </Tabs>
      </Box>
      
      {/* Tab Content */}
      <Box role="tabpanel" hidden={tabValue !== 0}>
        {tabValue === 0 && (
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Created</Typography>
                <Typography variant="body1">{formatDate(requirement.createdAt)}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Last Updated</Typography>
                <Typography variant="body1">{formatDate(requirement.updatedAt)}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                <Typography variant="body1">{getStatusLabel(requirement.status)}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Due Date</Typography>
                <Typography variant="body1">{formatDate(requirement.dueDate)}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Assigned To</Typography>
                <Typography variant="body1">{requirement.assignedTo || 'Not assigned'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Control</Typography>
                <Typography variant="body1">{requirement.controlName || 'None'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Obligation</Typography>
                <Typography variant="body1">{requirement.obligationName || 'None'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Framework</Typography>
                <Typography variant="body1">{requirement.frameworkName || 'None'}</Typography>
              </Grid>
            </Grid>
          </Paper>
        )}
      </Box>
      
      <Box role="tabpanel" hidden={tabValue !== 1}>
        {tabValue === 1 && (
          <Paper variant="outlined">
            {requirement.tasks && requirement.tasks.length > 0 ? (
              <List>
                {requirement.tasks.map((task, index) => (
                  <React.Fragment key={task.id}>
                    <ListItem
                      secondaryAction={
                        <IconButton 
                          edge="end" 
                          component={Link} 
                          to={`/tasks/${task.id}`}
                        >
                          <EditIcon />
                        </IconButton>
                      }
                    >
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center">
                            <Typography>{task.name}</Typography>
                            <Chip
                              label={getStatusLabel(task.status)}
                              color={getStatusColor(task.status) as any}
                              size="small"
                              sx={{ ml: 1 }}
                            />
                          </Box>
                        }
                        secondary={
                          <>
                            {task.description && (
                              <Typography variant="body2" sx={{ mt: 0.5 }}>
                                {task.description}
                              </Typography>
                            )}
                            {task.dueDate && (
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                Due: {formatDate(task.dueDate)}
                              </Typography>
                            )}
                          </>
                        }
                      />
                    </ListItem>
                    {index < requirement.tasks!.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            ) : (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  No tasks found for this requirement
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<TaskIcon />}
                  sx={{ mt: 2 }}
                  component={Link}
                  to={`/tasks/create?requirementId=${id}`}
                >
                  Add Task
                </Button>
              </Box>
            )}
          </Paper>
        )}
      </Box>
      
      <Box role="tabpanel" hidden={tabValue !== 2}>
        {tabValue === 2 && (
          <Paper variant="outlined" sx={{ p: 3 }}>
            {requirement.metadata && Object.keys(requirement.metadata).length > 0 ? (
              <Grid container spacing={3}>
                {Object.entries(requirement.metadata).map(([key, value]) => (
                  <Grid item xs={12} md={6} key={key}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {key.charAt(0).toUpperCase() + key.slice(1)}
                    </Typography>
                    <Typography variant="body1">{value}</Typography>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography color="text.secondary" align="center">
                No metadata available for this requirement
              </Typography>
            )}
          </Paper>
        )}
      </Box>
    </Box>
  );
}
